import { Alert, Button, Stack, Typography } from "@mui/material";
import { FundingPackageLock } from "./types";

type Props = {
  lock: FundingPackageLock | null;
  isLockedByMe: boolean;
  isLockedByOtherUser: boolean;
  hasIncomingTakeoverRequest: boolean;
  takeoverRequestedByName?: string | null;
  secondsUntilTakeoverAllowed: number;
  onRequestTakeover: () => Promise<void>;
  onAcceptTakeover: () => Promise<void>;
  onDeclineTakeover: () => Promise<void>;
  onForceTakeover: () => Promise<void>;
  onRelease: () => Promise<void>;
};

export default function FundingPackageLockBanner({
  lock,
  isLockedByMe,
  isLockedByOtherUser,
  hasIncomingTakeoverRequest,
  takeoverRequestedByName,
  secondsUntilTakeoverAllowed,
  onRequestTakeover,
  onAcceptTakeover,
  onDeclineTakeover,
  onForceTakeover,
  onRelease
}: Props) {
  if (!lock) return null;

  if (hasIncomingTakeoverRequest) {
    return (
      <Alert severity="warning" sx={{ mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
          <Typography variant="body2">
            {takeoverRequestedByName || "Another user"} is asking to take control of this funding package.
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button size="small" color="inherit" onClick={onDeclineTakeover}>
              Keep Mine
            </Button>
            <Button size="small" variant="contained" color="warning" onClick={onAcceptTakeover}>
              Release
            </Button>
          </Stack>
        </Stack>
      </Alert>
    );
  }

  if (isLockedByOtherUser) {
    const hasRequested = !!lock.takeoverRequestedAt;
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
          <Typography variant="body2">
            {lock.lockedByName || lock.lockedByEmail} is working on this funding package.
          </Typography>
          {hasRequested ? (
            <Button
              size="small"
              variant="contained"
              disabled={secondsUntilTakeoverAllowed > 0}
              onClick={onForceTakeover}
            >
              {secondsUntilTakeoverAllowed > 0
                ? `Take control in ${secondsUntilTakeoverAllowed}s`
                : "Take Control"}
            </Button>
          ) : (
            <Button size="small" variant="contained" onClick={onRequestTakeover}>
              Request Control
            </Button>
          )}
        </Stack>
      </Alert>
    );
  }

  return null;
}
